import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Copyright © 2025-2026 Cognizant Digital Business, Evolutionary AI.
 * All Rights Reserved.
 * Issued under the Academic Public License.
 *
 * You can be released from the terms, and requirements of the Academic Public
 * License by purchasing a commercial license.
 * Purchase of a commercial license is mandatory for any use of the
 * LMX Software in commercial settings.
 */
// END COPYRIGHT
import Box from "@mui/material/Box";
import { styled, useTheme } from "@mui/material/styles";
import ReactECharts from "echarts-for-react";
import { useCallback, useMemo } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import IdeasChartTooltip from "./ideas-chart/IdeasChartTooltip";
import useIdeasChart from "../hooks/useIdeasChart";
import useMediaQuery from "../hooks/useMediaQuery";
import { HelpIcon } from "../styles/HelpIcon";
import { LightTooltip } from "../styles/Tooltips";
// #region: Styled Components
const ChartContainer = styled(Box) `
    margin-bottom: 24px;
`;
const ChartWrapper = styled(Box)(({ theme }) => ({
    height: 500,
    width: "100%",
    position: "relative",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    background: theme.palette.background.paper,
    overflow: "hidden",
    padding: 16,
    // ensure chart library element fills the wrapper
    ".echarts-root": {
        height: "100% !important",
        width: "100% !important",
    },
}));
const Title = styled("h2")(({ theme }) => ({
    color: theme.palette.text.primary,
    fontSize: "1.5rem",
    margin: "0 0 16px 0",
    display: "flex",
    alignItems: "center",
}));
// #endregion: Types
// Module scope keeps the identity stable, so the chart option memo does not rebuild on every render.
const renderTooltip = (params) => renderToStaticMarkup(_jsx(IdeasChartTooltip, { ...params }));
const IdeasChart = ({ data }) => {
    const theme = useTheme();
    const humanColor = theme.palette.warning.main;
    const machineColor = theme.palette.primary.main;
    const paretoColor = theme.palette.error.main;
    const isNarrow = useMediaQuery("(max-width: 900px)");
    const option = useIdeasChart({
        data,
        isNarrow,
        humanColor,
        machineColor,
        paretoColor,
        renderTooltip,
    });
    const mixedGradientColor = useMemo(() => ({
        type: "linear",
        x: 0,
        y: 0,
        x2: 1,
        y2: 0,
        colorStops: [
            { offset: 0, color: humanColor },
            { offset: 0.5, color: humanColor },
            { offset: 0.5, color: machineColor },
            { offset: 1, color: machineColor },
        ],
        global: false,
    }), [humanColor, machineColor]);
    const onLegendSelectChanged = useCallback((params, chart) => {
        const selected = params.selected ?? {};
        const isHumanSelected = selected["Human Ideas"] !== false;
        const isMachineSelected = selected["AI Ideas"] !== false;
        const mixedAction = isHumanSelected || isMachineSelected ? "legendSelect" : "legendUnSelect";
        let mixedColor = mixedGradientColor;
        if (isHumanSelected && !isMachineSelected) {
            mixedColor = humanColor;
        }
        else if (!isHumanSelected && isMachineSelected) {
            mixedColor = machineColor;
        }
        chart.dispatchAction({
            type: mixedAction,
            name: "Mixed Ideas",
        });
        chart.setOption({
            series: [
                {
                    id: "mixed-ideas-series",
                    itemStyle: {
                        color: mixedColor,
                    },
                },
            ],
        });
    }, [humanColor, machineColor, mixedGradientColor]);
    const chartEvents = useMemo(() => ({
        legendselectchanged: onLegendSelectChanged,
    }), [onLegendSelectChanged]);
    return (_jsxs(ChartContainer, { children: [_jsxs(Title, { children: ["Ideas", _jsx(LightTooltip, { arrow: true, title: "This plot visualizes the ideas collected for this brainstorm session. " +
                            "Objective values are from 0 to 100, where 0 is the worst possible " +
                            "and 100 is the best possible. The most recent idea is highlighted. " +
                            "If there is more than one point on the Pareto front, they will be connected by a line.", children: _jsx(HelpIcon, { "aria-label": "Ideas chart help" }) })] }), _jsx(ChartWrapper, { children: _jsx(ChartWrapper, { children: _jsx(ReactECharts, { "data-testid": "ideas_chart-echarts_root-div", className: "echarts-root", "aria-label": "Ideas scatter chart", option: option, onEvents: chartEvents }) }) })] }));
};
export default IdeasChart;
